import { AnalyticsProvider } from '@anchorage/analytics';
import { handleError } from '@anchorage/sentry';
import { getMixpanelOptions } from 'constants/app';
import { useAppContext } from 'context/AppContext/AppContext';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { hashSessionId } from 'utils/hashSessionId';
import useIsPortoApp from 'utils/useIsPortoApp';

// The page URL and referrer carry the magic-link session ID, which is a bearer secret.
const MIXPANEL_PROPERTY_BLACKLIST = [
  '$current_url',
  '$referrer',
  '$initial_referrer',
];

type Props = {
  children: ReactNode;
};
const AnalyticsWrapper = ({ children }: Props) => {
  const { sessionInfo } = useAppContext();
  const isPorto = useIsPortoApp();
  const [userKeyId, setUserKeyId] = useState<string>();

  useEffect(() => {
    if (isPorto) {
      return undefined;
    }
    let cancelled = false;
    setUserKeyId(undefined);
    hashSessionId(sessionInfo.access)
      .then((hashed) => {
        if (!cancelled) {
          setUserKeyId(hashed);
        }
      })
      .catch((error: unknown) => {
        handleError(
          new Error('Failed to hash the session ID for analytics', {
            cause: error,
          }),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [isPorto, sessionInfo.access]);

  if (isPorto) {
    return <>{children}</>;
  }

  return (
    <AnalyticsProvider
      user={{ userKeyId }}
      options={{
        enabled: true,
        ...getMixpanelOptions(),
        propertyBlacklist: MIXPANEL_PROPERTY_BLACKLIST,
      }}
    >
      {children}
    </AnalyticsProvider>
  );
};

export default AnalyticsWrapper;
